import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchStaffCanvasStatus, provisionStaffToCanvas } from './services/lms-provisioning.service';

const STATUS = {
  IDLE: 'IDLE',
  SUBMITTING: 'SUBMITTING',
  SUCCESS: 'SUCCESS',
  ERROR: 'ERROR',
};

/**
 * Drives the staff "Provision to Canvas" action (CC-14598, §8.1). The hook:
 *
 *  - Loads the per-facility Canvas admin provisioning snapshot from
 *    `GET /staff/:personId/provision-canvas` on mount so the staff profile
 *    can swap the still-clickable provision button for a "Provisioned to
 *    Canvas" badge once every active FACILITY_ADMIN membership has been
 *    granted.
 *  - Calls the portal POST that runs `handleAdminCreated` in-process for each
 *    active FACILITY_ADMIN membership; idempotent, so re-click is safe.
 *  - Performs a single background snapshot refresh after a successful POST
 *    (no polling). Operators can reload the page to pick up later changes.
 */
const useStaffCanvasProvisionHook = (personId, { enabled = true } = {}) => {
  const [status, setStatus] = useState(STATUS.IDLE);
  const [error, setError] = useState(null);
  const [canvasStatus, setCanvasStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const refreshInFlightRef = useRef(false);

  const refreshCanvasStatus = useCallback(async ({ background = false } = {}) => {
    if (!enabled || personId == null) {
      return null;
    }
    if (refreshInFlightRef.current) {
      return null;
    }
    refreshInFlightRef.current = true;
    if (!background) {
      setStatusLoading(true);
    }
    try {
      const snapshot = await fetchStaffCanvasStatus(personId);
      setCanvasStatus(snapshot);
      return snapshot;
    } catch (err) {
      if (process.env.NODE_ENV !== 'test') {
        // eslint-disable-next-line no-console
        console.warn('[staff-canvas-status] refresh failed', err);
      }
      return null;
    } finally {
      refreshInFlightRef.current = false;
      if (!background) {
        setStatusLoading(false);
      }
    }
  }, [personId, enabled]);

  useEffect(() => {
    if (!enabled || personId == null) {
      return undefined;
    }
    refreshCanvasStatus();
    return undefined;
  }, [refreshCanvasStatus, enabled, personId]);

  const provision = useCallback(async () => {
    if (!enabled || personId == null) {
      return null;
    }
    setStatus(STATUS.SUBMITTING);
    setError(null);
    try {
      const result = await provisionStaffToCanvas(personId);
      setStatus(STATUS.SUCCESS);
      await refreshCanvasStatus({ background: true });
      return result;
    } catch (err) {
      setError(err);
      setStatus(STATUS.ERROR);
      throw err;
    }
  }, [personId, refreshCanvasStatus, enabled]);

  const reset = useCallback(() => {
    setStatus(STATUS.IDLE);
    setError(null);
  }, []);

  const allProvisioned = Boolean(canvasStatus?.allProvisioned);
  const totalFacilities = canvasStatus?.totalFacilities ?? 0;
  const provisionedFacilities = canvasStatus?.provisionedFacilities ?? 0;

  return {
    status,
    error,
    provision,
    reset,
    refreshCanvasStatus,
    canvasStatus,
    statusLoading,
    allProvisioned,
    totalFacilities,
    provisionedFacilities,
    isSubmitting: status === STATUS.SUBMITTING,
    isSuccess: status === STATUS.SUCCESS,
  };
};

useStaffCanvasProvisionHook.STATUS = STATUS;

export default useStaffCanvasProvisionHook;
